import { useState, useRef, useEffect, useMemo } from "react";
import {
    Plus, Ruler as RulerIcon, Search, X,
    Trash2, Pencil, Check, Sparkles, ArrowRight, Copy,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
    SelectGroup, SelectLabel, SelectSeparator,
} from "@/components/ui/select";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { DndContext, closestCenter, PointerSensor, KeyboardSensor, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, arrayMove, verticalListSortingStrategy, sortableKeyboardCoordinates } from "@dnd-kit/sortable";
import { toast } from "sonner";
import { useSizing } from "@/lib/sizing-store";
import { MEASUREMENT_KINDS, MEASUREMENT_KIND_MAP, PRODUCT_MEASURE_LABEL, PRODUCT_MEASURE_HINT, KIND_GROUP_LABELS, isProductLengthKind } from "@/lib/measurement-kinds";
import { PageHeader, EmptyState, PrimaryButton, SortableRow } from "@/components/design-system";
import {
    TemplateRenameDialog, AddItemDialog, BlockersDialog, CloneDialog,
} from "@/components/sizing/TemplateActionDialogs";
import { InlineTitleEdit } from "@/components/sizing/InlineTitleEdit";

const SAMPLE_HINTS = [
    {
        name: "Kadın Üst Giyim",
        sample: [
            { name: "Göğüs" },
            { name: "Bel" },
            { name: "Kalça" },
            { name: "Boy" },
        ],
    },
    {
        name: "Jean & Pantolon",
        sample: [
            { name: "Bel" },
            { name: "Kalça" },
            { name: "Bacak İçi" },
            { name: "Bacak Boyu" },
        ],
    },
    {
        name: "Ayakkabı",
        sample: [
            { name: "Ayak Uzunluğu" },
            { name: "Ayak Genişliği" },
        ],
    },
];

export default function MeasurementGroups() {
    const {
        groups, tables, addGroup, updateGroup, removeGroup,
        renameMeasureInGroup, addMeasureToGroup, deleteMeasureFromGroup, cloneGroup,
    } = useSizing();
    const [activeId, setActiveId] = useState(null);
    const [dialogState, setDialogState] = useState(null);
    const [confirmDelete, setConfirmDelete] = useState(null);
    const [query, setQuery] = useState("");
    const [renameState, setRenameState] = useState(null);
    // Add-measure with opt-in table picker.
    const [addMeasureState, setAddMeasureState] = useState(null);
    // 409 blockers from delete-group or delete-measure.
    const [blockers, setBlockers] = useState(null);
    // Clone-group dialog.
    const [cloneState, setCloneState] = useState(null);

    // Usage count per group — how many tables were sourced from it (explicit
    // FK first, legacy label fallback).
    const usageByGroupId = useMemo(() => {
        const map = new Map();
        (tables || []).forEach((t) => {
            let gid = t.sourceGroupId;
            if (!gid) {
                const match = groups.find((g) => g.name === t.groupLabel);
                if (match) gid = match.id;
            }
            if (gid) map.set(gid, (map.get(gid) || 0) + 1);
        });
        return map;
    }, [tables, groups]);
    const tablesUsingMeasure = (groupId, measureName) => {
        const g = groups.find((x) => x.id === groupId);
        return (tables || []).filter((t) => {
            const linked = t.sourceGroupId === groupId
                || (!t.sourceGroupId && g && t.groupLabel === g.name);
            return linked && (t.measures || []).includes(measureName);
        });
    };
    // All tables sourced from a given group, regardless of which measures
    // they contain. Used by the add-measure modal.
    const tablesUsingGroup = (groupId) => {
        const g = groups.find((x) => x.id === groupId);
        return (tables || []).filter((t) => (
            t.sourceGroupId === groupId
            || (!t.sourceGroupId && g && t.groupLabel === g.name)
        ));
    };

    useEffect(() => {
        if (groups.length && !groups.find((g) => g.id === activeId)) {
            setActiveId(groups[0].id);
        }
        if (!groups.length) setActiveId(null);
    }, [groups, activeId]);

    const filtered = groups.filter((g) =>
        !query.trim() ||
        g.name.toLowerCase().includes(query.toLowerCase())
    );
    const current = groups.find((g) => g.id === activeId) || null;

    // Reorder measures via drag-and-drop — same set, different order.
    const reorderMeasures = (nextMeasures) => {
        if (!current) return;
        updateGroup(current.id, { measures: nextMeasures });
    };
    // Update per-measure metadata (kind + howToMeasure + half) — names unchanged,
    // so the "reorder-only" backend guard passes.
    const updateMeasureMeta = (idx, meta) => {
        if (!current) return;
        const next = (current.measures || []).map((m, i) => {
            if (i !== idx) return m;
            const base = typeof m === "string" ? { name: m } : { ...m };
            const patched = { ...base };
            if ("kind" in meta) {
                if (meta.kind) patched.kind = meta.kind;
                else delete patched.kind;
            }
            if ("howToMeasure" in meta) {
                const val = (meta.howToMeasure || "").trim();
                if (val) patched.howToMeasure = val;
                else delete patched.howToMeasure;
            }
            if ("half" in meta) {
                // half=true → merchant tabloyu yarım çevre girer (örn. 'Bel ½').
                // Backend _get_cell ×2 ile tam çevreye çevirir. False/null → sakla.
                if (meta.half === true) patched.half = true;
                else delete patched.half;
            }
            return patched;
        });
        updateGroup(current.id, { measures: next });
    };
    const removeMeasure = async (idx) => {
        if (!current) return;
        const removed = current.measures[idx];
        try {
            const res = await deleteMeasureFromGroup(current.id, removed.name);
            toast.success(
                (res?.tablesUpdated || 0) > 0
                    ? `${removed.name} ölçüsü ${res.tablesUpdated} tablodan da temizlendi`
                    : `${removed.name} ölçüsü "${current.name}" grubundan çıkarıldı`
            );
        } catch (e) {
            if (e?.response?.status === 409) {
                const payload = e.response.data?.detail;
                setBlockers({
                    title: `"${removed.name}" ölçüsü silinemez`,
                    description: typeof payload === "object" ? payload?.detail : payload,
                    blockers: (typeof payload === "object" ? payload?.blockers : []) || [],
                    forkLabel: "Bu grubu kopyala",
                    forkKind: "group",
                    forkId: current.id,
                });
                return;
            }
            toast.error(e?.response?.data?.detail || `${removed.name} silinemedi`);
        }
    };
    const addMeasureInline = (val) => {
        if (!current || !val.trim()) return;
        if (current.measures.some((m) => m.name === val.trim())) {
            toast.error("Bu ölçü zaten ekli");
            return;
        }
        setAddMeasureState({ defaultName: val.trim() });
    };

    return (
        <TooltipProvider delayDuration={200}>
            <div className="space-y-6" data-testid="measurement-groups-page">
                <PageHeader
                    icon={RulerIcon}
                    title="Ölçü Grupları"
                    description="Beden tablolarında sütun olarak görünecek ölçü setlerini tanımla — örn. üst giyim için Göğüs/Bel/Kalça, pantolon için Bel/Bacak İçi. Tablo oluştururken bu setten istediğin ölçüleri tek tıkla seçersin."
                    actions={
                        <PrimaryButton
                            onClick={() => setDialogState({ mode: "create" })}
                            data-testid="measurement-group-create-btn"
                        >
                            <Plus className="h-3.5 w-3.5" /> Yeni Grup
                        </PrimaryButton>
                    }
                />

                {groups.length === 0 ? (
                    <GroupsEmptyState
                        onCreate={() => setDialogState({ mode: "create" })}
                        onUseSample={(s) =>
                            setDialogState({
                                mode: "create",
                                initial: { name: s.name, measures: s.sample },
                            })
                        }
                    />
                ) : (
                    <div className="grid grid-cols-1 lg:grid-cols-[340px_1fr] gap-6">
                        <div className="rounded-xl border border-border bg-surface overflow-hidden self-start">
                            <div className="px-3 py-2.5 border-b border-border">
                                <div className="relative">
                                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                                    <Input
                                        placeholder="Grup ara…"
                                        className="h-8 pl-8 text-xs bg-background"
                                        value={query}
                                        onChange={(e) => setQuery(e.target.value)}
                                        data-testid="group-search"
                                    />
                                </div>
                            </div>
                            <div className="divide-y divide-border max-h-[560px] overflow-y-auto scroll-thin">
                                {filtered.map((g) => {
                                    const usage = usageByGroupId.get(g.id) || 0;
                                    return (
                                    <button
                                        key={g.id}
                                        onClick={() => setActiveId(g.id)}
                                        className={`w-full text-left px-4 py-3 transition-colors ${activeId === g.id ? "bg-muted/60" : "hover:bg-muted/30"}`}
                                        data-testid={`measurement-group-${g.id}`}
                                    >
                                        <div className="flex items-center justify-between gap-2">
                                            <div className="font-display text-sm font-semibold tracking-tight truncate">{g.name}</div>
                                            <Badge variant="outline" className="text-[10px] font-mono tracking-wider shrink-0">{g.measures.length}</Badge>
                                        </div>
                                        <div className="mt-2 flex flex-wrap gap-1">
                                            {g.measures.slice(0, 4).map((m) => (
                                                <span key={m.name} className="text-[10px] inline-flex items-center rounded-md border border-foreground/15 bg-surface px-1.5 py-0.5">{m.name}</span>
                                            ))}
                                            {g.measures.length > 4 && (
                                                <span className="text-[10px] text-muted-foreground/60 self-center">+{g.measures.length - 4}</span>
                                            )}
                                        </div>
                                        {usage > 0 && (
                                            <div className="mt-2 text-[10px] text-muted-foreground inline-flex items-center gap-1">
                                                <span className="h-1 w-1 rounded-full bg-brand" aria-hidden="true" /> {usage} tabloda kullanılıyor
                                            </div>
                                        )}
                                    </button>
                                    );
                                })}
                                {filtered.length === 0 && (
                                    <div className="px-4 py-8 text-center text-xs text-muted-foreground">
                                        &ldquo;{query}&rdquo; için sonuç yok
                                    </div>
                                )}
                            </div>
                        </div>

                        {current && (
                            <GroupDetail
                                current={current}
                                usage={usageByGroupId.get(current.id) || 0}
                                tables={tables}
                                onRename={async (newName) => {
                                    // Inline group title rename via PUT — only
                                    // name is patched, measures untouched so
                                    // the permutation-guard on the backend
                                    // accepts the patch.
                                    await updateGroup(current.id, { name: newName });
                                    toast.success(`Grup adı "${newName}" olarak güncellendi`);
                                }}
                                onDelete={() => {
                                    // Skip the misleading two-step "Evet, Sil"
                                    // for used groups — jump straight to the
                                    // blockers list. Same UX as size delete.
                                    const usage = usageByGroupId.get(current.id) || 0;
                                    if (usage === 0) {
                                        setConfirmDelete(current);
                                        return;
                                    }
                                    const linked = tablesUsingGroup(current.id);
                                    setBlockers({
                                        title: `"${current.name}" silinemez`,
                                        description: "Bu ölçü grubu tabloda kullanılıyor — önce bu tabloları sil ya da grubu kopyalayarak yeni bir sürüm oluştur.",
                                        blockers: linked.map((t) => ({ id: t.id, name: t.name })),
                                        forkLabel: "Bu grubu kopyala",
                                        forkKind: "group",
                                        forkId: current.id,
                                    });
                                }}
                                onClone={() => setCloneState({
                                    kind: "group",
                                    id: current.id,
                                    title: `${current.name} — kopyala`,
                                    description: "Aynı ölçülerle yeni bir grup oluşturulur. Mevcut tablolar etkilenmez.",
                                    defaultName: `${current.name} (kopya)`,
                                })}
                                onReorder={reorderMeasures}
                                onRemoveMeasure={removeMeasure}
                                onAddMeasure={addMeasureInline}
                                onUpdateMeasureMeta={updateMeasureMeta}
                                onRenameMeasure={(oldName, newName) => {
                                    const affected = tablesUsingMeasure(current.id, oldName);
                                    setRenameState({
                                        entityKind: "group",
                                        entityId: current.id,
                                        entityName: current.name,
                                        oldName, newName, affected,
                                    });
                                }}
                            />
                        )}
                    </div>
                )}
            </div>

            <GroupDialog
                key={dialogState?.mode + (dialogState?.initial?.id || "new")}
                state={dialogState}
                tables={tables}
                onClose={() => setDialogState(null)}
                onRequestPropagate={(groupId, groupName, oldName, newName) => {
                    const affected = tablesUsingMeasure(groupId, oldName);
                    setRenameState({
                        entityKind: "group",
                        entityId: groupId,
                        entityName: groupName,
                        oldName, newName, affected,
                    });
                }}
                onSubmit={(values) => {
                    if (dialogState.mode === "edit") {
                        // Same "reorder-only" rule as SeriesDialog — server 400s
                        // on adds/removes via PUT so we send `measures` only
                        // when the set is unchanged (pure reorder or rename
                        // already propagated).
                        const orig = dialogState.initial?.measures || [];
                        const nameOf = (m) => (typeof m === "string" ? m : m?.name);
                        const origNames = new Set(orig.map(nameOf));
                        const nextNames = new Set((values.measures || []).map(nameOf));
                        const permutation = orig.length === (values.measures || []).length &&
                            [...origNames].every((n) => nextNames.has(n));
                        const patch = { name: values.name };
                        if (permutation) patch.measures = values.measures;
                        updateGroup(dialogState.initial.id, patch);
                        toast.success(`${values.name} güncellendi`);
                    } else {
                        const created = addGroup(values);
                        setActiveId(created.id);
                        toast.success(`${values.name} oluşturuldu`);
                    }
                    setDialogState(null);
                }}
            />

            {/* Delete-group confirmation. Only reached for UNUSED groups —
                used groups jump straight to BlockersDialog (see onDelete above). */}
            <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>{confirmDelete?.name} silinsin mi?</AlertDialogTitle>
                        <AlertDialogDescription>
                            Bu grubu hiçbir tablo kullanmıyor — güvenle silinebilir.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Vazgeç</AlertDialogCancel>
                        <AlertDialogAction
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                            onClick={async (e) => {
                                e.preventDefault();
                                if (!confirmDelete) return;
                                const target = confirmDelete;
                                try {
                                    await removeGroup(target.id);
                                    toast.success(`${target.name} silindi`);
                                    if (activeId === target.id) setActiveId(null);
                                } catch (err) {
                                    // Race guard — table linked between check
                                    // and delete → fall through to Blockers.
                                    if (err?.response?.status === 409) {
                                        const payload = err.response.data?.detail;
                                        setBlockers({
                                            title: `"${target.name}" silinemez`,
                                            description: typeof payload === "object" ? payload?.detail : payload,
                                            blockers: (typeof payload === "object" ? payload?.tables : []) || [],
                                            forkLabel: "Bu grubu kopyala",
                                            forkKind: "group",
                                            forkId: target.id,
                                        });
                                    } else {
                                        toast.error("Silme başarısız");
                                    }
                                }
                                setConfirmDelete(null);
                            }}
                        >
                            Evet, Sil
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            <TemplateRenameDialog
                state={renameState}
                onClose={() => setRenameState(null)}
                onSubmit={async () => {
                    if (!renameState) return;
                    try {
                        const res = await renameMeasureInGroup(
                            renameState.entityId,
                            renameState.oldName,
                            renameState.newName,
                        );
                        toast.success(
                            (res?.tablesUpdated || 0) > 0
                                ? `"${renameState.oldName}" → "${renameState.newName}" · ${res.tablesUpdated} tabloda güncellendi`
                                : `"${renameState.oldName}" → "${renameState.newName}" olarak yeniden adlandırıldı`
                        );
                    } catch (_) { /* store already toasted */ }
                    finally { setRenameState(null); }
                }}
                onFork={() => {
                    if (!renameState) return;
                    const g = groups.find((x) => x.id === renameState.entityId);
                    setCloneState({
                        kind: "group",
                        id: renameState.entityId,
                        title: `${g?.name || "Grup"} — kopyala`,
                        description: "Aynı ölçülerle yeni bir grup oluşturulur. Mevcut tablolar etkilenmez.",
                        defaultName: `${g?.name || "Grup"} (kopya)`,
                    });
                    setRenameState(null);
                }}
            />

            <AddItemDialog
                open={!!addMeasureState}
                onClose={() => setAddMeasureState(null)}
                itemKind="measure"
                defaultName={addMeasureState?.defaultName || ""}
                linkedTables={current ? tablesUsingGroup(current.id).map((t) => ({ id: t.id, name: t.name })) : []}
                existingNames={(current?.measures || []).map((m) => (m.name || m))}
                onSubmit={async ({ name, addToTables }) => {
                    if (!current) return;
                    try {
                        const res = await addMeasureToGroup(current.id, name, addToTables);
                        const n = res?.tablesUpdated || 0;
                        toast.success(
                            n > 0
                                ? `${name} ölçüsü eklendi · ${n} tabloya da eklendi`
                                : `${name} ölçüsü ${current.name} grubuna eklendi`
                        );
                        setAddMeasureState(null);
                    } catch (_) { /* store toasted */ }
                }}
            />

            <BlockersDialog
                state={blockers}
                onClose={() => setBlockers(null)}
                onFork={blockers?.forkKind === "group" ? () => {
                    const g = groups.find((x) => x.id === blockers.forkId);
                    setCloneState({
                        kind: "group",
                        id: blockers.forkId,
                        title: `${g?.name || "Grup"} — kopyala`,
                        description: "Aynı ölçülerle yeni bir grup oluşturulur. Mevcut tablolar etkilenmez.",
                        defaultName: `${g?.name || "Grup"} (kopya)`,
                    });
                    setBlockers(null);
                } : undefined}
            />

            <CloneDialog
                state={cloneState}
                onClose={() => setCloneState(null)}
                onSubmit={async (newName) => {
                    if (!cloneState) return;
                    try {
                        const cloned = await cloneGroup(cloneState.id, newName);
                        toast.success(`${cloned.name} oluşturuldu`);
                        if (cloned?.id) setActiveId(cloned.id);
                    } catch (_) { /* store toasted */ }
                    setCloneState(null);
                }}
            />
        </TooltipProvider>
    );
}

const GroupsEmptyState = ({ onCreate, onUseSample }) => (
    <EmptyState
        icon={RulerIcon}
        title="Henüz ölçü grubu yok"
        description="Ölçü grupları, tabloda sütun olarak görünecek setlerdir. Üst giyimde Göğüs/Bel/Kalça, pantolonda Bel/Bacak İçi gibi. Bir kere tanımla, ürünlere tablo eklerken istediğin ölçüleri seç."
        action={{
            label: "İlk Grubu Oluştur",
            onClick: onCreate,
            icon: Plus,
            testId: "empty-create-group",
        }}
        testId="group-empty-state"
        secondary={
            <>
                <div className="text-[10px] font-semibold tracking-wider uppercase text-muted-foreground mb-3 inline-flex items-center gap-1.5">
                    <Sparkles className="h-3 w-3" strokeWidth={2} /> Hızlı Başlangıç Şablonları
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2.5">
                    {SAMPLE_HINTS.map((s) => (
                        <button
                            key={s.name}
                            onClick={() => onUseSample(s)}
                            className="text-left rounded-lg border border-border bg-surface p-3.5 hover:border-foreground/30 transition-colors group"
                            data-testid={`sample-${s.name}`}
                        >
                            <div className="flex items-center justify-between">
                                <div className="font-display text-sm font-semibold tracking-tight">{s.name}</div>
                                <ArrowRight className="h-3.5 w-3.5 text-muted-foreground group-hover:text-foreground transition-colors" />
                            </div>
                            <div className="mt-2 flex flex-wrap gap-1">
                                {s.sample.map((m) => (
                                    <span key={m.name} className="text-[10px] font-mono inline-flex items-center justify-center rounded border border-border bg-background px-1.5 py-0.5">{m.name}</span>
                                ))}
                            </div>
                        </button>
                    ))}
                </div>
            </>
        }
    />
);

const GroupDetail = ({ current, usage = 0, tables = [], onRename, onDelete, onClone, onReorder, onRemoveMeasure, onAddMeasure, onUpdateMeasureMeta, onRenameMeasure }) => {
    const [inline, setInline] = useState("");
    const inputRef = useRef(null);
    // Per-measure usage counts — how many tables sourced from this group
    // currently have this exact measure in their snapshot.
    const usageByMeasure = useMemo(() => {
        const m = new Map();
        (tables || []).forEach((t) => {
            const linked = t.sourceGroupId === current?.id
                || (!t.sourceGroupId && current && t.groupLabel === current.name);
            if (!linked) return;
            (t.measures || []).forEach((x) => m.set(x, (m.get(x) || 0) + 1));
        });
        return m;
    }, [tables, current]);

    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
    );

    const handleDragEnd = ({ active, over }) => {
        if (!over || active.id === over.id) return;
        const oldIndex = current.measures.findIndex((m) => m.name === active.id);
        const newIndex = current.measures.findIndex((m) => m.name === over.id);
        if (oldIndex < 0 || newIndex < 0) return;
        onReorder(arrayMove(current.measures, oldIndex, newIndex));
    };

    return (
        <div className="space-y-4" data-testid="group-detail">
            <div className="rounded-xl border border-border bg-surface">
            <div className="px-5 pt-5 pb-3 border-b border-border flex items-start justify-between gap-3">
                <div className="min-w-0">
                    <div className="font-display text-base font-semibold tracking-tight">
                        <InlineTitleEdit
                            value={current.name}
                            onSave={onRename}
                            testId="group-title-edit"
                        />
                    </div>
                    {usage > 0 && (
                        <div className="mt-1 text-[10.5px] text-muted-foreground inline-flex items-center gap-1.5" data-testid="group-usage-chip">
                            <span className="h-1.5 w-1.5 rounded-full bg-brand" aria-hidden="true" />
                            {usage} tabloda kullanılıyor · <span className="text-foreground/70">tüm tablolar bu grubu takip eder</span>
                        </div>
                    )}
                </div>
                <div className="flex items-center gap-1 shrink-0">
                    <Button variant="outline" size="sm" className="h-8 text-xs gap-1.5" onClick={onClone} data-testid="group-clone-btn">
                        <Copy className="h-3 w-3" /> Kopyala
                    </Button>
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-destructive" onClick={onDelete} data-testid="group-delete-btn">
                        <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                </div>
            </div>
            <div className="p-5">
                <div className="text-[11px] font-semibold tracking-wider uppercase text-muted-foreground mb-3">
                    Ölçüler · sıralama ({current.measures.length})
                </div>
                {current.measures.length === 0 ? (
                    <div className="rounded-lg border border-dashed border-border px-5 py-8 text-center">
                        <p className="text-xs text-muted-foreground">Bu grupta henüz ölçü yok. Aşağıdan ekle.</p>
                    </div>
                ) : (
                    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                        <SortableContext items={current.measures.map((m) => m.name)} strategy={verticalListSortingStrategy}>
                            <div className="rounded-lg border border-border overflow-hidden" data-testid="measures-list">
                                {current.measures.map((m, i) => (
                                    <SortableRow
                                        key={m.name}
                                        id={m.name}
                                        index={i}
                                        label={m.name}
                                        usageCount={usageByMeasure.get(m.name) || 0}
                                        onRemove={() => onRemoveMeasure(i)}
                                        onRename={onRenameMeasure}
                                        testIdPrefix="measure"
                                    />
                                ))}
                            </div>
                        </SortableContext>
                    </DndContext>
                )}
                <form
                    onSubmit={(e) => {
                        e.preventDefault();
                        onAddMeasure(inline);
                        setInline("");
                        setTimeout(() => inputRef.current?.focus(), 0);
                    }}
                    className="mt-3 flex items-center gap-2"
                >
                    <Input
                        ref={inputRef}
                        value={inline}
                        onChange={(e) => setInline(e.target.value)}
                        placeholder="örn. Omuz, Kol Boyu, Yaka Açıklığı"
                        className="h-8 text-xs bg-background flex-1 max-w-[260px]"
                        data-testid="inline-measure-input"
                    />
                    <Button type="submit" size="sm" variant="outline" className="h-8 text-xs gap-1.5" disabled={!inline.trim()} data-testid="inline-measure-add">
                        <Plus className="h-3 w-3" /> Ekle
                    </Button>
                </form>
            </div>
            </div>

            {current.measures.length > 0 && (
                <MeasureMetaSection
                    measures={current.measures}
                    onUpdateMeta={onUpdateMeasureMeta}
                />
            )}
        </div>
    );
};

/**
 * SortableMeasureRow has been extracted to
 * `@/components/design-system/SortableRow` — the canonical DnD row used by
 * both `MeasurementGroups` (measures) and `Sizes` (bedenler). Keep this comment
 * so future consumers know where to look.
 */

const GroupDialog = ({ state, onClose, onSubmit, tables = [], onRequestPropagate }) => {
    const isEdit = state?.mode === "edit";
    const [name, setName] = useState(state?.initial?.name || "");
    const [measures, setMeasures] = useState(state?.initial?.measures || []);
    const [mInput, setMInput] = useState("");
    const mRef = useRef(null);
    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
    );

    // Downstream usage per measure — used to render the "N tabloda" chip
    // inside the edit dialog. Recomputes when the parent's `tables` array
    // changes (e.g. after a rename propagates and the store refreshes).
    const usageByMeasure = useMemo(() => {
        const m = new Map();
        if (!isEdit || !state?.initial?.id) return m;
        const groupId = state.initial.id;
        const groupName = state.initial.name;
        (tables || []).forEach((t) => {
            const linked = t.sourceGroupId === groupId
                || (!t.sourceGroupId && t.groupLabel === groupName);
            if (!linked) return;
            (t.measures || []).forEach((mv) => m.set(mv, (m.get(mv) || 0) + 1));
        });
        return m;
    }, [tables, state, isEdit]);

    useEffect(() => {
        if (state) {
            setName(state.initial?.name || "");
            setMeasures(state.initial?.measures || []);
            setMInput("");
        }
    }, [state]);

    if (!state) return null;

    const addMeasure = () => {
        const v = mInput.trim();
        if (!v) return;
        if (measures.some((m) => m.name === v)) {
            toast.error("Bu ölçü zaten ekli");
            return;
        }
        setMeasures((arr) => [...arr, { name: v }]);
        setMInput("");
        mRef.current?.focus();
    };

    const removeAt = (i) => setMeasures((arr) => arr.filter((_, idx) => idx !== i));
    // Inline rename in the dialog — local mutation for immediate UI feedback +
    // if the measure is used in downstream tables (usageCount > 0), ALSO
    // delegates to the parent so it can open the RenamePropagateDialog and
    // let the merchant choose whether to sync the name across tables.
    const renameAt = (oldName, newName) => {
        if (!newName || newName === oldName) return;
        if (measures.some((m) => (m.name || m) === newName)) {
            toast.error(`"${newName}" bu grupta zaten var`);
            return;
        }
        setMeasures((arr) => arr.map((m) => (
            (m.name || m) === oldName
                ? (typeof m === "object" ? { ...m, name: newName } : { name: newName })
                : m
        )));
        if (isEdit && state?.initial?.id && (usageByMeasure.get(oldName) || 0) > 0) {
            onRequestPropagate && onRequestPropagate(
                state.initial.id,
                state.initial.name,
                oldName,
                newName,
            );
        }
    };
    const handleDragEnd = ({ active, over }) => {
        if (!over || active.id === over.id) return;
        const oldIndex = measures.findIndex((m) => m.name === active.id);
        const newIndex = measures.findIndex((m) => m.name === over.id);
        if (oldIndex < 0 || newIndex < 0) return;
        setMeasures((arr) => arrayMove(arr, oldIndex, newIndex));
    };

    const canSave = name.trim() && measures.length > 0;

    return (
        <Dialog open onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-w-xl p-0 gap-0 overflow-hidden" data-testid="group-dialog">
                <div className="px-6 py-5 border-b border-border flex items-start justify-between gap-3">
                    <div>
                        <div className="text-[10px] font-semibold tracking-wider uppercase text-muted-foreground">
                            {isEdit ? "Grup Düzenleniyor" : "Yeni Ölçü Grubu"}
                        </div>
                        <DialogTitle className="font-display text-lg font-bold tracking-tight mt-0.5">
                            {isEdit ? state.initial.name : "Hangi Ölçüleri Kullanacaksın?"}
                        </DialogTitle>
                    </div>
                </div>

                <div className="px-6 py-5 max-h-[480px] overflow-y-auto scroll-thin space-y-4">
                    <Field label="Grup Adı" required>
                        <Input
                            autoFocus={!isEdit}
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder="örn. Kadın Üst Giyim"
                            className="h-10 text-sm"
                            data-testid="group-name-input"
                        />
                    </Field>
                    <Field
                        label="Ölçüler"
                        required
                        hint={
                            isEdit
                                ? "Sıralamak için sürükle. Ölçü ekleme/silme detay panelinden yapılır."
                                : "Adı yaz ve Enter'a bas. Sıralamak için sürükleyip bırak."
                        }
                    >
                        <div className="space-y-4">
                            {!isEdit && (
                                <div className="grid grid-cols-[1fr_auto] gap-2 items-center">
                                    <Input
                                        ref={mRef}
                                        value={mInput}
                                        onChange={(e) => setMInput(e.target.value)}
                                        onKeyDown={(e) => {
                                            if (e.key === "Enter") {
                                                e.preventDefault();
                                                addMeasure();
                                            }
                                        }}
                                        placeholder="Ölçü adı"
                                        className="h-10 text-sm"
                                        data-testid="dialog-measure-name"
                                    />
                                    <Button
                                        type="button"
                                        onClick={addMeasure}
                                        variant="outline"
                                        className="h-10 text-xs gap-1.5"
                                        disabled={!mInput.trim()}
                                        data-testid="dialog-measure-add"
                                    >
                                        <Plus className="h-3 w-3" /> Ekle
                                    </Button>
                                </div>
                            )}
                            {measures.length > 0 && (
                                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                                    <SortableContext items={measures.map((m) => m.name)} strategy={verticalListSortingStrategy}>
                                        <div className="rounded-lg border border-border overflow-hidden">
                                            {measures.map((m, i) => (
                                                <SortableRow
                                                    key={m.name}
                                                    id={m.name}
                                                    index={i}
                                                    label={m.name}
                                                    usageCount={usageByMeasure.get(m.name) || 0}
                                                    onRemove={isEdit ? undefined : () => removeAt(i)}
                                                    onRename={renameAt}
                                                    testIdPrefix="dialog-measure"
                                                />
                                            ))}
                                        </div>
                                    </SortableContext>
                                </DndContext>
                            )}
                        </div>
                    </Field>
                </div>

                <div className="px-6 py-4 border-t border-border bg-muted/20 flex items-center justify-between gap-3">
                    <div className="text-[11px] text-muted-foreground">
                        {measures.length} ölçü eklendi
                    </div>
                    <div className="flex items-center gap-2">
                        <Button variant="ghost" size="sm" onClick={onClose} className="h-9 text-xs">Vazgeç</Button>
                        <Button
                            size="sm"
                            onClick={() => onSubmit({ name, measures })}
                            disabled={!canSave}
                            className="h-9 text-xs gap-1.5 bg-brand hover:bg-brand/90 text-brand-fg"
                            data-testid="group-dialog-save"
                        >
                            <Check className="h-3 w-3" /> {isEdit ? "Güncelle" : "Grubu Oluştur"}
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
};

const Field = ({ label, hint, required, children }) => (
    <div className="space-y-1.5">
        <div className="flex items-center justify-between">
            <label className="text-[11px] font-semibold tracking-wider uppercase text-muted-foreground">
                {label}{required && <span className="text-foreground/40 ml-0.5">*</span>}
            </label>
        </div>
        {children}
        {hint && <p className="text-[11px] text-muted-foreground">{hint}</p>}
    </div>
);

/**
 * MeasureMetaSection — kanonik ölçü etiketi + "Nasıl Ölçülür" açıklama editörü.
 *
 * - Ölçü Tipi (kind): 12 etiketli kanonik taksonomi. Bedenimi Bul sihirbazı
 *   `side === "body"` etiketlerini vücut sorusu tetikleyicisi olarak okur.
 * - Nasıl Ölçülür (howToMeasure): opsiyonel Türkçe açıklama. Boşsa Beden Tablosu
 *   "Nasıl Ölçülür" bloğunda o satır gösterilmez.
 *
 * Değişiklikler blur/change'de anında persist eder (onUpdateMeta callback).
 */
const MeasureMetaSection = ({ measures, onUpdateMeta }) => {
    const bodyCount = measures.filter((m) => MEASUREMENT_KIND_MAP[m.kind]).length;
    const totalCount = measures.length;

    return (
        <div className="rounded-xl border border-border bg-surface" data-testid="measure-meta-section">
            <div className="px-5 pt-5 pb-3 border-b border-border">
                <div className="flex items-center justify-between gap-3">
                    <div>
                        <div className="font-display text-sm font-semibold tracking-tight">
                            Ölçü Türü ve Ölçüm Rehberi
                        </div>
                        <p className="text-[11.5px] text-muted-foreground mt-0.5 leading-relaxed max-w-2xl">
                            Her ölçünün vücut ölçüsü mü, ürün ölçüsü mü olduğunu belirt — Bedenimi Bul sihirbazı sadece vücut ölçüleri için soru sorar.
                            Nasıl ölçülür açıklaması varsa Beden Tablosu&apos;nda rehber olarak görünür.
                        </p>
                    </div>
                    <Badge variant="outline" className="text-[10px] tabular-nums shrink-0" data-testid="measure-meta-body-count">
                        {bodyCount}/{totalCount} vücut ölçüsü
                    </Badge>
                </div>
            </div>
            <div className="p-5 space-y-3">
                {measures.map((m, i) => (
                    <MeasureMetaRow
                        key={m.name}
                        measure={m}
                        index={i}
                        onUpdate={(meta) => onUpdateMeta(i, meta)}
                    />
                ))}
            </div>
        </div>
    );
};

const MeasureMetaRow = ({ measure, index, onUpdate }) => {
    const [howToDraft, setHowToDraft] = useState(measure.howToMeasure || "");
    // Sync local draft when the measure changes (e.g. after refresh).
    useEffect(() => {
        setHowToDraft(measure.howToMeasure || "");
    }, [measure.howToMeasure]);
    // Debounced auto-save — persists 500ms after the user stops typing so we
    // don't fire an API call on every keystroke, and don't require a blur
    // event to trigger persistence.
    useEffect(() => {
        const current = measure.howToMeasure || "";
        if (howToDraft === current) return;
        const t = setTimeout(() => {
            onUpdate({ howToMeasure: howToDraft });
        }, 500);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [howToDraft]);

    const kindInfo = measure.kind ? MEASUREMENT_KIND_MAP[measure.kind] : null;
    const kindGroup = kindInfo?.group || null;
    const isProductLength = isProductLengthKind(measure.kind);
    const isHalf = measure.half === true;

    // Dropdown grupları — body ve product_length kind'ları ayrı başlıklar altında.
    const bodyKinds = MEASUREMENT_KINDS.filter((k) => k.group === "body");
    const productLengthKinds = MEASUREMENT_KINDS.filter((k) => k.group === "product_length");

    return (
        <div
            className="rounded-lg border border-border/80 bg-background p-3.5"
            data-testid={`measure-meta-row-${index}`}
        >
            <div className="grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-3.5">
                {/* Sol: ölçü adı + etiket + yarım checkbox */}
                <div className="space-y-2">
                    <div className="text-[13px] font-semibold text-foreground truncate">
                        {measure.name}
                    </div>
                    <Select
                        value={measure.kind || "__product__"}
                        onValueChange={(v) => onUpdate({ kind: v === "__product__" ? null : v })}
                    >
                        <SelectTrigger
                            className="h-8 text-xs bg-surface"
                            data-testid={`measure-meta-kind-${index}`}
                        >
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectGroup>
                                <SelectLabel className="text-[10px] uppercase tracking-wider text-muted-foreground/80">
                                    {KIND_GROUP_LABELS.body}
                                </SelectLabel>
                                {bodyKinds.map((k) => (
                                    <SelectItem
                                        key={k.value}
                                        value={k.value}
                                        className="text-xs"
                                        data-testid={`measure-meta-kind-opt-${k.value}`}
                                    >
                                        {k.label}
                                    </SelectItem>
                                ))}
                            </SelectGroup>
                            <SelectSeparator />
                            <SelectGroup>
                                <SelectLabel className="text-[10px] uppercase tracking-wider text-muted-foreground/80">
                                    {KIND_GROUP_LABELS.product_length}
                                </SelectLabel>
                                {productLengthKinds.map((k) => (
                                    <SelectItem
                                        key={k.value}
                                        value={k.value}
                                        className="text-xs"
                                        data-testid={`measure-meta-kind-opt-${k.value}`}
                                    >
                                        {k.label}
                                    </SelectItem>
                                ))}
                            </SelectGroup>
                            <SelectSeparator />
                            <SelectItem
                                value="__product__"
                                className="text-xs"
                                data-testid="measure-meta-kind-opt-product"
                            >
                                {PRODUCT_MEASURE_LABEL}
                            </SelectItem>
                        </SelectContent>
                    </Select>
                    <div className="text-[10.5px] text-muted-foreground leading-snug">
                        {kindGroup === "body" && (
                            <span className="inline-flex items-center gap-1">
                                <span className="h-1 w-1 rounded-full bg-brand" /> Sihirbaz vücut sorusu tetikler
                            </span>
                        )}
                        {kindGroup === "product_length" && (
                            <span className="inline-flex items-center gap-1" style={{ color: "hsl(var(--brand))" }}>
                                <span className="h-1 w-1 rounded-full bg-brand" /> Kullanıcı boyu zorunlu olur
                            </span>
                        )}
                        {!kindGroup && (
                            <span className="inline-flex items-center gap-1 opacity-70">
                                <span className="h-1 w-1 rounded-full bg-muted-foreground" /> {PRODUCT_MEASURE_HINT}
                            </span>
                        )}
                    </div>
                    {/* ½ Yarım değer checkbox — merchant tabloyu yarım çevre girerse (örn. "Bel ½ 42.5") */}
                    <label
                        className="flex items-start gap-2 cursor-pointer group pt-1"
                        data-testid={`measure-meta-half-label-${index}`}
                    >
                        <input
                            type="checkbox"
                            checked={isHalf}
                            onChange={(e) => onUpdate({ half: e.target.checked })}
                            className="mt-0.5 h-3.5 w-3.5 rounded border-border accent-brand cursor-pointer"
                            data-testid={`measure-meta-half-${index}`}
                        />
                        <div className="leading-snug">
                            <div className="text-[11.5px] font-medium text-foreground group-hover:text-brand transition-colors">
                                ½ Yarım değer
                            </div>
                            <div className="text-[10px] text-muted-foreground mt-0.5">
                                Değer yarım çevre ise işaretle — hesaplamada ×2 ile tam çevreye çevrilir
                            </div>
                        </div>
                    </label>
                </div>

                {/* Sağ: Nasıl ölçülür */}
                <div className="space-y-1.5">
                    <label
                        htmlFor={`htm-${index}`}
                        className="text-[10.5px] font-semibold uppercase tracking-wider text-muted-foreground"
                    >
                        Nasıl Ölçülür <span className="text-foreground/40 normal-case tracking-normal">(opsiyonel)</span>
                    </label>
                    <Textarea
                        id={`htm-${index}`}
                        value={howToDraft}
                        onChange={(e) => setHowToDraft(e.target.value)}
                        placeholder="örn. Kolların altından, göğüs çevrenin en geniş yerini yatay çekmeden ölç."
                        rows={2}
                        className="text-xs bg-surface resize-none min-h-[60px]"
                        data-testid={`measure-meta-howto-${index}`}
                    />
                </div>
            </div>
        </div>
    );
};
